import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useVariables } from '../../../../providers/VariableProvider'
import { useFilterPersistence } from './useFilterPersistence'

export const FilterSelectorContext = createContext({
  options: [],
  selectedOption: null,
  setSelectedOption: () => {},
  applyTo: 'accountManagementOverrides',
  variableValue: [],
  selectedValues: [],
  setSelectedValues: () => {},
  showDetails: false,
  setShowDetails: () => {},
  anchorRef: { current: null },
  maxShow: 2,
  selectedText: 'Selected: ',
  moreText: 'items selected',
  hasMultipleOptions: true,
  pendingRestore: null,
  consumePendingRestore: () => {},
  clearPersistence: () => {}
})

export const useFilterSelectContext = () => {
  return useContext(FilterSelectorContext)
}

export const useFilterSelectorValues = ({
  options,
  applyTo,
  defaultSelected,
  maxShow = 2,
  selectedText = 'Selected: ',
  moreText = 'items selected',
  persistKey
}) => {
  const { setVariable, getVariable } = useVariables()
  const persistence = useFilterPersistence(persistKey)

  const [selectedOption, setSelectedOption] = useState(defaultSelected || options?.[0]?.value || null)
  const [variableValue, setVariableValue] = useState([])
  const [selectedValues, setSelectedValues] = useState([])
  const [showDetails, setShowDetails] = useState(false)
  const [pendingRestore, setPendingRestore] = useState(null)
  const anchorRef = useRef(null)

  const didInitialLoad = useRef(false)
  const wasRestoredFromCacheRef = useRef(false)
  const hasSyncedOnceRef = useRef(false)
  const justClearedRef = useRef(false)
  const lastSavedSelectedOptionRef = useRef(null)
  const lastPersistenceRef = useRef(persistence)

  const hasMultipleOptions = useMemo(() => {
    return options && options.length > 1
  }, [options])

  // Must run before the load effect — load short-circuits on didInitialLoad
  // and otherwise wouldn't re-read the new identity's cache.
  useEffect(() => {
    if (lastPersistenceRef.current === persistence) return
    didInitialLoad.current = false
    hasSyncedOnceRef.current = false
    justClearedRef.current = false
    lastSavedSelectedOptionRef.current = null
    wasRestoredFromCacheRef.current = false
    setSelectedOption(defaultSelected || options?.[0]?.value || null)
    setSelectedValues([])
    setVariableValue([])
    setPendingRestore(null)
    setVariable(applyTo, [])
    lastPersistenceRef.current = persistence
  }, [persistence, defaultSelected, options, applyTo, setVariable])

  useEffect(() => {
    if (!persistence.isEnabled) return
    if (didInitialLoad.current) return
    const blob = persistence.load()
    if (!blob) {
      didInitialLoad.current = true
      return
    }
    const optionStillExists = (options || []).some((opt) => opt.value === blob.selectedOption)
    if (!optionStillExists) {
      persistence.clear()
      didInitialLoad.current = true
      return
    }
    const restoredVariableValue = blob.variableValue || []
    setSelectedOption(blob.selectedOption)
    setSelectedValues(blob.selectedValues || [])
    setVariableValue(restoredVariableValue)
    // Same reference to both setters so variable-sync's variableValue !==
    // getVariable() check sees them as equal and doesn't fire a redundant write.
    setVariable(applyTo, restoredVariableValue)
    setPendingRestore({
      filterType: blob.selectedOption,
      selectedValues: blob.selectedValues || []
    })
    wasRestoredFromCacheRef.current = true
    didInitialLoad.current = true
  }, [persistence, options, applyTo, setVariable])

  useEffect(() => {
    // Skip when load just restored: load already wrote the AE variable, and
    // our setVariable(applyTo, []) here would clobber it via batched-update
    // ordering in the same effect cycle.
    if (wasRestoredFromCacheRef.current) return
    if (!getVariable(applyTo)) {
      setVariable(applyTo, [])
    }
  }, [applyTo, getVariable, setVariable])

  useEffect(() => {
    if (wasRestoredFromCacheRef.current) {
      wasRestoredFromCacheRef.current = false
      hasSyncedOnceRef.current = true
      lastSavedSelectedOptionRef.current = selectedOption
      return
    }
    const variableChanged = variableValue !== getVariable(applyTo)
    const optionChangedSinceSave = lastSavedSelectedOptionRef.current !== selectedOption
    if ((variableChanged || optionChangedSinceSave) && !showDetails) {
      if (variableChanged) {
        setVariable(applyTo, variableValue || [])
      }
      if (justClearedRef.current) {
        justClearedRef.current = false
      } else if (
        hasSyncedOnceRef.current &&
        persistence.isEnabled &&
        // Skip while restoring; FilterSelector hasn't applied survivors yet.
        pendingRestore === null
      ) {
        // FilterTypeSelector clears selectedValues but leaves variableValue
        // pointing at the previous filter type's formatted output. Persisting
        // that would later restore a "no selection" UI alongside a stale AE
        // variable that still filters by the old type. Empty selection ↔ empty
        // variableValue is the only safe contract here.
        const valueToSave = selectedValues.length > 0 ? variableValue : []
        persistence.save({ selectedOption, selectedValues, variableValue: valueToSave })
      }
      hasSyncedOnceRef.current = true
      lastSavedSelectedOptionRef.current = selectedOption
    }
  }, [applyTo, variableValue, setVariable, getVariable, showDetails, persistence, selectedOption, selectedValues, pendingRestore])

  const consumePendingRestore = useCallback(() => {
    setPendingRestore(null)
  }, [])

  const clearPersistence = useCallback(() => {
    persistence.clear()
    justClearedRef.current = true
    // Drop any in-flight restore; otherwise FilterSelector's restore effect
    // will consume it once its pipeline loads and undo the Reset.
    setPendingRestore(null)
  }, [persistence])

  return useMemo(() => ({
    options,
    selectedOption,
    setSelectedOption,
    applyTo,
    variableValue,
    setVariableValue,
    selectedValues,
    setSelectedValues,
    showDetails,
    setShowDetails,
    anchorRef,
    maxShow,
    selectedText,
    moreText,
    hasMultipleOptions,
    pendingRestore,
    consumePendingRestore,
    clearPersistence
  }), [
    options,
    selectedOption,
    applyTo,
    variableValue,
    selectedValues,
    showDetails,
    maxShow,
    selectedText,
    moreText,
    hasMultipleOptions,
    pendingRestore,
    consumePendingRestore,
    clearPersistence
  ])
}
